import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { useSession } from '../state/useSession';
import { useBuildRun } from '../state/buildRun';
import { useDraft } from '../state/draft';
import { redeemCode, type PreparedApp } from '../api/billing';
import { redeemFailureMessage } from '@/components/account/accountView';
import { Button, Screen, TextField } from '../ui';

/** "Thu, Nov 7", in the reader's own time zone. */
function day(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

/**
 * /redeem — where a code from an email is used (owner, 2026-10-08). Signed in by the route (with
 * an email code or Apple), the code already in the field from the link (`?code=`), one press.
 *
 * A personal code (infra shared/personalCodes.ts) may carry an app already built for this person:
 * it is finished into their account here, by their own browser, exactly as a build is (buildRun
 * `start` with `prebuilt`), which publishes it to its unlisted link and opens the project.
 */
export default function Redeem() {
  const [params] = useSearchParams();
  const { user } = useSession();
  const [code, setCode] = useState((params.get('code') ?? '').trim().toUpperCase());
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<{ line: string; finishing: string | null } | null>(null);

  function finish(app: PreparedApp, ownerId: string): void {
    const draft = useDraft.getState();
    draft.reset();
    useDraft.setState({ description: app.goal, appName: app.appName, plan: app.plan ?? null });
    void useBuildRun.getState().start(
      ownerId,
      {
        description: app.goal,
        appName: app.appName,
        plan: app.plan ?? null,
        vibe: null,
        styleBrief: null,
        kind: 'personal',
        attachedDoc: null,
        attachedDocs: [],
        attachedMedia: [],
        reservedId: app.projectId ?? null,
      },
      { prebuilt: { appId: app.appId, appName: app.appName, html: app.html, appCode: app.appCode } },
    );
  }

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    const typed = code.trim();
    if (!typed || busy || !user) return;
    setBusy(true);
    setProblem(null);
    try {
      const result = await redeemCode(typed);
      if (result.status !== 'redeemed') {
        setProblem(redeemFailureMessage(result.reason));
        return;
      }
      const until = day(result.gift?.expiresAt);
      const line = result.alreadyRedeemed
        ? until ? `This code is already on your account — free until ${until}.` : 'This code is already on your account.'
        : result.gift && until
          ? `Your free month is on: ${result.granted.toLocaleString()} credits to build and change your apps, until ${until}. No card needed.`
          : `${result.granted.toLocaleString()} credits added to your account.`;
      setDone({ line, finishing: result.prepared ? result.prepared.appName : null });
      if (result.prepared) finish(result.prepared, user.id);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen title="Redeem your code">
      {done ? (
        <div className="redeem-done">
          <p role="status" className="type-body">{done.line}</p>
          {done.finishing && (
            <p className="type-body">Putting {done.finishing} in your account — it opens in a moment.</p>
          )}
        </div>
      ) : (
        <form className="redeem" onSubmit={(e) => void submit(e)} noValidate>
          <p className="type-body">Your code is from Saddle. Redeem it on the account it was sent to.</p>
          <TextField
            label="Code"
            value={code}
            onChangeText={(v) => setCode(v.toUpperCase())}
            autoComplete="off"
            error={problem ?? undefined}
          />
          <Button title="Redeem code" type="submit" loading={busy} disabled={!code.trim()} />
        </form>
      )}
    </Screen>
  );
}
